import { z } from 'zod';
import { leadSideSchema } from '../roles';
/**
 * KOMMUNIKATION — het Vorlagen-register (`mail_vorlagen`), de afzenderprofielen
 * (`mail_absender_profile`) en het Protokoll. Owner-akkoord 26-8-2026
 * (KOMMUNIKATION-PIPELINE-ARCHITECTUUR §1.1).
 *
 * ── TWEE LAGEN, ÉÉN REGISTER ────────────────────────────────────────────────
 * Elke rij hoort bij precies één tenant. De rijen van de HOLDING-tenant zijn de
 * platform-sjablonen (CRM-Einladung, Passwort-Reset, Login-Alarm, …) en zijn
 * voor elke tenant LEESBAAR maar nooit schrijfbaar (RLS-policy, zie de
 * migratie). Een tenant zonder eigen rij voor een sleutel gebruikt de
 * holding-rij; «überschreiben» kopieert haar naar de tenant-laag met
 * `basisVorlageId` als herkomst. Het antwoord van `GET /kommunikation/vorlagen`
 * draagt daarom per rij een `ebene` — de client hoeft de holding-tenant niet
 * te kennen om grijs van bewerkbaar te onderscheiden.
 *
 * ── WAT HIER BEWUST NIET STAAT ──────────────────────────────────────────────
 * Geen render-parameters per sleutel en geen verzend-contract: dat is PR-2
 * (render-service + `POST /internal/mail/render`). PR-1 leest alleen.
 */
// ── Vocabulaire ──────────────────────────────────────────────────────────────
/** Mirrors the Prisma `MailVorlageSoort` enum. */
export const MAIL_VORLAGE_SOORTEN = ['transaktional', 'automation', 'newsletter'];
export const mailVorlageSoortSchema = z.enum(MAIL_VORLAGE_SOORTEN);
export const MAIL_VORLAGE_SOORT_LABELS = {
    transaktional: 'Transaktional',
    automation: 'Automation',
    newsletter: 'Newsletter',
};
/** Mirrors the Prisma `DkimStatus` enum. */
export const DKIM_STATUS = ['unbekannt', 'geprueft', 'fehlt'];
export const dkimStatusSchema = z.enum(DKIM_STATUS);
/** Welke laag een rij van het register is — afgeleid door de server. */
export const MAIL_VORLAGE_EBENEN = ['holding', 'tenant'];
/**
 * Sleutel: stabiel, per tenant uniek. Kleine letters, cijfers, `_`, `-` en `.`
 * (`portal.invited`, `e1.rechner_ergebnis`, vrije marketing-sleutels).
 */
export const vorlageSchluesselSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9_.-]*$/, { message: 'key_invalid' });
/**
 * Trigger van een sjabloon (dossier §1.1): een EVENT van de events-catalogus,
 * of een PIPELINE-stufe (PR-3). `null` = handmatig/nieuwsbrief.
 */
export const mailVorlageTriggerSchema = z.discriminatedUnion('art', [
    z
        .object({
        art: z.literal('event'),
        event: z
            .string()
            .trim()
            .min(3)
            .max(80)
            .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, { message: 'event_invalid' }),
    })
        .strict(),
    z
        .object({
        art: z.literal('pipeline'),
        stufeKey: z
            .string()
            .trim()
            .min(1)
            .max(80)
            .regex(/^[a-z0-9][a-z0-9_-]*$/, { message: 'stufe_invalid' }),
        side: leadSideSchema.nullable(),
    })
        .strict(),
]);
// ── Vorlagen: schrijfcontracten ──────────────────────────────────────────────
const naamSchema = z.string().trim().min(1).max(160);
const betreffSchema = z.string().trim().min(1).max(300);
const bodySchema = z.string().trim().min(1).max(200_000);
const beschreibungSchema = z.string().trim().max(500);
/** Create (POST /kommunikation/vorlagen) — altijd in de EIGEN tenant-laag. */
export const mailVorlageCreateSchema = z
    .object({
    schluessel: vorlageSchluesselSchema,
    soort: mailVorlageSoortSchema.default('automation'),
    name: naamSchema,
    betreff: betreffSchema,
    bodyHtml: bodySchema,
    beschreibung: beschreibungSchema.nullish(),
    /**
     * PR-3: de per-stufe-automation is `trigger.art = 'pipeline'` (stufeKey +
     * side); de oude kolommen `triggerStatus/triggerSide` zijn gemigreerd en
     * weg — één trigger-vorm, één lezer (auto-send).
     */
    trigger: mailVorlageTriggerSchema.nullish(),
    absenderProfilId: z.string().uuid().nullish(),
    aktiv: z.boolean().default(true),
})
    .strict();
/** Partial update (PATCH /kommunikation/vorlagen/:id) — alleen tenant-rijen. */
export const mailVorlageUpdateSchema = z
    .object({
    schluessel: vorlageSchluesselSchema,
    soort: mailVorlageSoortSchema,
    name: naamSchema,
    betreff: betreffSchema,
    bodyHtml: bodySchema,
    beschreibung: beschreibungSchema.nullable(),
    trigger: mailVorlageTriggerSchema.nullable(),
    absenderProfilId: z.string().uuid().nullable(),
    aktiv: z.boolean(),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/**
 * De LAAG van een holding-rij (PATCH /control/kommunikation/vorlagen/:id/plattform).
 *
 * BEWUST GEEN VELD IN `mailVorlageUpdateSchema` (review #495 MINOR-A): dat
 * schema bedient óók de tenant-route, en `plattform` opent estate-breed
 * leesrecht in de RLS-SELECT-tak. Publiceren is bovendien geen inhoudswijziging
 * maar een BESLUIT — «vanaf nu erft elke dochter deze sjabloon» — en dat hoort
 * een eigen, geauditeerde handeling te zijn in plaats van een vinkje dat met een
 * typefout in een onderwerpregel meelift. `false` is dezelfde weg terug.
 */
export const mailVorlagePlattformSchema = z.object({ plattform: z.boolean() }).strict();
/**
 * DKIM-stand van een afzenderprofiel — ALLEEN het Control Center
 * (PATCH /control/kommunikation/tenants/:tenantId/absender/:id/dkim). Een
 * owner-gate-signaal: `geprueft` zegt dat het afzenderdomein in Brevo
 * geverifieerd is, en dat verklaart een platform-beheerder na meting, nooit de
 * tenant zelf (review #495 MINOR-3).
 */
export const mailAbsenderDkimSchema = z.object({ dkimStatus: dkimStatusSchema }).strict();
/** «Überschreiben»: kopieer een holding-rij naar de eigen laag. */
export const mailVorlageUeberschreibenSchema = z
    .object({ basisVorlageId: z.string().uuid() })
    .strict();
// ── Afzenderprofielen ────────────────────────────────────────────────────────
export const mailAbsenderProfilCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().toLowerCase().email().max(254),
    replyTo: z.string().trim().toLowerCase().email().max(254).nullish(),
    standard: z.boolean().default(false),
})
    .strict();
export const mailAbsenderProfilUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().toLowerCase().email().max(254),
    replyTo: z.string().trim().toLowerCase().email().max(254).nullable(),
    standard: z.boolean(),
    // GEEN `dkimStatus` (review #495 MINOR-3): een owner-gate-signaal mag de
    // tenant niet zelf verklaren. PR-2 laat het Control Center hem zetten.
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
// ── Protokoll ────────────────────────────────────────────────────────────────
export const PROTOKOLL_SEITENGROESSE = 50;
/**
 * `seite` begrensd op 100 (= 5000 regels per ledger): de lezer haalt per bron
 * `seite × grootte` rijen en voegt in geheugen samen (review e64cb8f6 M-1).
 * Niet `.strict()`: een cache-buster of UTM in de query is geen 400 waard.
 */
export const protokollQuerySchema = z.object({
    seite: z.coerce.number().int().min(1).max(100).default(1),
});
// ── PR-2: renderen voor de router (`POST /internal/mail/render`) ────────────
/**
 * DE ROUTER WORDT DOM (dossier §1.1, laatste alinea van «Render-service»).
 *
 * Vandaag draagt `finavest-event-router.json` twee dingen die er niet horen:
 * Brevo-template-id's (`CFG.templates`) en complete HTML-body's (`e8Html`,
 * `rueckrufHtml`, de `mlgMail`-schil). Beide zijn kopieën van iets waarvan het
 * register de bron is, en een kopie die niemand meet loopt uit elkaar — precies
 * de klasse die PR-1 heeft opgeruimd voor `email_templates`.
 *
 * Vanaf PR-2 vraagt de router per event ÉÉN keer: «geef me deze sleutels,
 * gerenderd, voor deze tenant». Hij bepaalt nog steeds ontvangers en routing;
 * onderwerp, HTML en afzender komen uit het register.
 *
 * ── ÉÉN CALL PER EVENT, NIET PER MAIL ───────────────────────────────────────
 * Een event levert zelden meer dan één klantmail op, maar het plan is een
 * lijst; een call per mail zou de router-graaf per item moeten splitsen. Deze
 * vorm houdt de omzet bij vier nieuwe nodes in één rechte lijn.
 *
 * ── FAIL-CLOSED, PER POSTEN ─────────────────────────────────────────────────
 * Ontbreekt een sjabloon (of staat hij op inactief, of heeft de tenant geen
 * afzenderprofiel), dan komt die posten terug met `gefunden: false` + reden —
 * niet met een half gerenderde mail. De router degradeert dan naar de interne
 * ops-melding die hij vandaag ook voor een ontbrekend gegeven stuurt: liever
 * zichtbaar niets dan onzichtbaar iets verkeerds.
 */
export const MAIL_RENDER_MAX_POSTEN = 10;
/** Eén token-waarde: kort genoeg om een mail geen payload-kanaal te maken. */
// Geen CR/LF: het ONDERWERP wordt bewust niet ge-escaped, en een regeleinde in
// een kopveld is bij een SMTP-provider header-injectie (review PR-2 NIT).
const renderParamWertSchema = z
    .string()
    .max(2000)
    .regex(/^[^\r\n]*$/, 'no_line_breaks');
// ── B6 (owner 29-9): de drie INTERNE meldingen uit het register ─────────────
/**
 * DRIE INTERNE MELDINGEN, ÉÉN PER GEBEURTENIS (owner 29-9, na de E2E-test).
 * Het team kreeg drie platte tekstmails; sinds B6 zijn het HTML-mails uit het
 * register (holding-laag, `{{marke_*}}`), met genummerde stappen: Bewertung 1/2
 * (`valuation.lead.created`), Bewertung 2/2 (`valuation.estimated`) en de
 * Beratung (`consult.requested`) als eigen soort.
 *
 * ── DE GEGEVENS KOMEN UIT DE DATABASE, NIET UIT HET EVENT ───────────────────
 * De Bewertungs-events zijn PII-vrij. De router stuurt daarom alleen een
 * VERWIJZING (`leadId`) en wat alleen HIJ weet (het event-id, de aanleiding, of
 * hij de E8-klantmail verstuurt, de Beratungs-wens); de render-service leest de
 * rest server-side (`InternMeldungDatenService`). Welke parameters per sleutel
 * mogen, staat hieronder — STRICT: een onbekende parameter of een ontbrekende
 * `leadId` is een 400, geen stil genegeerd veld.
 *
 * De vierde, dezelfde weg: de Vor-Ort-melding (`vor_ort.requested`,
 * docs/VOR-ORT-FORMULIER-CONTRACT.md §5.2 Mail 2). Het event draagt de keuzes
 * van het formulier (§2.1) maar geen telefoonnummer (§5.1); de router stuurt de
 * keuzes als technische waarden, het backend maakt er labels van en leest het
 * contact uit de database.
 */
export const INTERN_MELDUNG_SCHLUESSEL = [
    'intern.bewertung_anfrage',
    'intern.bewertung_schaetzung',
    'intern.beratung',
    'intern.vor_ort',
    // Order E1 (17-10): de teammail van de Objekt-Anfrage — het event is PII-vrij,
    // dus ALLES (anliegen, object, contact, nachricht, taak) komt uit de database.
    'intern.objekt_anfrage',
];
export function istInternMeldung(schluessel) {
    return INTERN_MELDUNG_SCHLUESSEL.includes(schluessel);
}
/**
 * De AANVRAAG-parameters per interne sleutel (router → render). Dit zijn géén
 * sjabloon-tokens: de tokens in het HTML vult de render-service zelf uit de
 * database (`vorlagen-katalog.ts` noemt ze per rij onder `parameter`).
 *
 * `phone` bij de Beratung: het nummer dat de aanvrager in het Beratungs-
 * formulier typte. Een BESTAANDE lead (Rechner-stap 3, het Beratungs-widget op
 * het Bewertungs-resultaat) wordt door `/ingest/beratung` niet bijgewerkt — de
 * database kent dan een ander of géén nummer, en het terugbelnummer staat
 * alleen in het event (en in de titel van de Rückruf-Aufgabe).
 */
export const INTERN_MELDUNG_PARAMETER = {
    'intern.bewertung_anfrage': ['leadId', 'eventId', 'anlass'],
    'intern.bewertung_schaetzung': ['leadId', 'eventId', 'anlass', 'e8Versendet', 'berichtLink'],
    'intern.beratung': [
        'leadId',
        'eventId',
        'anlass',
        'channel',
        'preferredTime',
        'topic',
        'language',
        'triggerSource',
        'phone',
    ],
    // De §2.1-waarden uit het event (technisch; `erreichbarkeit` mag leeg zijn) —
    // geen naam, geen e-mail, geen telefoon: die staan in de database.
    'intern.vor_ort': [
        'leadId',
        'eventId',
        'anlass',
        'objektart',
        'plz',
        'zeitraum',
        'erreichbarkeit',
    ],
    // Order E1 (17-10): alleen de verwijzing — de lead is 1:1 met zijn aanvraag
    // (`objekt_anfragen.leadId` is uniek), dus de backend vindt de rij zelf.
    'intern.objekt_anfrage': ['leadId', 'eventId', 'anlass'],
};
/**
 * De aanleiding die de router per sleutel mag melden. `cross_sell` = het event
 * droeg `crossSell: true` (de lead gaf de MLG ↔ Finavest-toestemming); de router
 * ziet niet of de lead via de SMS-code of via «Akzeptieren» in de Eingang kwam —
 * dat leest de render-service uit de database (`phoneVerifiedAt`).
 */
export const INTERN_MELDUNG_ANLASS = {
    'intern.bewertung_anfrage': ['anfrage', 'cross_sell'],
    'intern.bewertung_schaetzung': ['schaetzung'],
    'intern.beratung': ['beratung'],
    'intern.vor_ort': ['vor_ort'],
    'intern.objekt_anfrage': ['objekt_anfrage'],
};
/**
 * ORDER E1 (17-10): KLANTMAILS DIE UIT DE DATABASE RENDEREN — de B6-vorm, maar
 * dan voor een KLANT. E12 (`e12.objekt_anfrage_bestaetigung`) draagt een kopie
 * van wat de persoon typte, inclusief een meerregelige nachricht van tot 2000
 * tekens; zoiets past niet door een render-parameter (geen CR/LF, max. 2000,
 * HTML-escaped) en hoort ook niet in een event. De router stuurt daarom
 * uitsluitend een VERWIJZING (`anfrageId`); de render-service leest de rij, het
 * object en de ontvanger zelf, toetst tenant en `istTest`, en geeft de
 * ontvanger in het antwoord terug (`empfaenger`).
 *
 * STRICT, zoals de interne sleutels: een andere parameter, een ontbrekende of
 * misvormde `anfrageId` of een meegestuurde `empfaenger` is een 400 — een
 * ontvanger uit de aanvraag zou de database als bron van waarheid omzeilen.
 */
export const DB_KUNDENMAIL_SCHLUESSEL = ['e12.objekt_anfrage_bestaetigung'];
export function istDbKundenmail(schluessel) {
    return DB_KUNDENMAIL_SCHLUESSEL.includes(schluessel);
}
/** De ENIGE aanvraag-parameter per DB-klantmail — de verwijzing, niets anders. */
export const DB_KUNDENMAIL_PARAMETER = {
    'e12.objekt_anfrage_bestaetigung': ['anfrageId'],
};
/** Ja/nein-vlaggen van de router (E8 verstuurd, Bericht-link aanwezig). */
const INTERN_JA_NEIN = ['e8Versendet', 'berichtLink'];
const leadIdSchema = z.string().uuid();
export const mailRenderPostenSchema = z
    .object({
    schluessel: vorlageSchluesselSchema,
    /**
     * Per-sleutel-parameters (`vorlagen-katalog.ts` noemt ze per rij) náást de
     * standaardtokens. Waarden worden bij het renderen HTML-ge-escaped.
     */
    params: z.record(z.string().regex(/^[a-zA-Z0-9_]+$/), renderParamWertSchema).default({}),
    /**
     * Alleen om het Protokoll een GEMASKEERDE ontvanger te geven — en alleen bij
     * een klant- of stafmail: een interne sleutel (`intern.*`) met een
     * ontvanger is een 400 (zie hieronder).
     */
    empfaenger: z.string().trim().toLowerCase().email().max(254).optional(),
})
    .strict()
    .superRefine((posten, ctx) => {
    // Order E1: een DB-klantmail heeft óók een GESLOTEN lijst — precies de
    // verwijzing — en GEEN ontvanger in de aanvraag (de backend leest hem).
    if (istDbKundenmail(posten.schluessel)) {
        if (posten.empfaenger !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'db_empfaenger_verboten',
                path: ['empfaenger'],
            });
        }
        const erlaubtDb = DB_KUNDENMAIL_PARAMETER[posten.schluessel];
        for (const naam of Object.keys(posten.params)) {
            if (!erlaubtDb.includes(naam)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'db_parameter_unbekannt',
                    path: ['params', naam],
                });
            }
        }
        if (!leadIdSchema.safeParse(posten.params.anfrageId).success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'db_anfrage_id_fehlt',
                path: ['params', 'anfrageId'],
            });
        }
        return;
    }
    // B6: alleen de interne sleutels hebben een GESLOTEN parameterlijst; de
    // klant- en stafmails houden hun open record (de router stuurt daar ook de
    // standaardtokens `vorname`/`email`/…).
    if (!istInternMeldung(posten.schluessel))
        return;
    // Een interne melding gaat naar het TEAM, nooit naar een klant: een
    // ontvanger in de aanvraag is PII die hier niets te zoeken heeft — en het
    // Protokoll zou het (gemaskeerde) klantadres als ontvanger van een teammail
    // tonen (RVB6 MAJOR-1). 400 voor de hele aanvraag, geen stil genegeerd veld.
    if (posten.empfaenger !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'intern_empfaenger_verboten',
            path: ['empfaenger'],
        });
    }
    const erlaubt = INTERN_MELDUNG_PARAMETER[posten.schluessel];
    for (const naam of Object.keys(posten.params)) {
        if (!erlaubt.includes(naam)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'intern_parameter_unbekannt',
                path: ['params', naam],
            });
        }
    }
    if (!leadIdSchema.safeParse(posten.params.leadId).success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'intern_lead_id_fehlt',
            path: ['params', 'leadId'],
        });
    }
    const anlass = posten.params.anlass;
    if (anlass !== undefined && !INTERN_MELDUNG_ANLASS[posten.schluessel].includes(anlass)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'intern_anlass_unbekannt',
            path: ['params', 'anlass'],
        });
    }
    for (const naam of INTERN_JA_NEIN) {
        const wert = posten.params[naam];
        if (wert !== undefined && wert !== 'ja' && wert !== 'nein') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'intern_ja_nein',
                path: ['params', naam],
            });
        }
    }
    if ((posten.params.eventId ?? '').length > 64) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'intern_event_id_zu_lang',
            path: ['params', 'eventId'],
        });
    }
});
export const mailRenderAnfrageSchema = z
    .object({
    /** De tenant van de envelop; slug (`mlg`) of id — de router kent beide. */
    tenantId: z.string().uuid().optional(),
    tenantSlug: z.string().trim().max(60).optional(),
    /** Uit de envelop, alleen voor het Protokoll-spoor. */
    eventTyp: z.string().trim().max(80).optional(),
    eventId: z.string().trim().max(64).optional(),
    mails: z.array(mailRenderPostenSchema).min(1).max(MAIL_RENDER_MAX_POSTEN),
})
    .strict()
    .refine((v) => Boolean(v.tenantId ?? v.tenantSlug), {
    message: 'tenant_required',
    path: ['tenantId'],
});
/** Waarom een posten niet gerenderd kon worden — de router logt de reden. */
export const MAIL_RENDER_GRUENDE = [
    'tenant_unbekannt',
    'vorlage_fehlt',
    'vorlage_inaktiv',
    'absender_fehlt',
    // B6: een interne melding verwijst naar een lead die in DEZE tenant niet
    // bestaat — geen gegevens, dus geen HTML; de router stuurt zijn tekstversie.
    'lead_fehlt',
    // B6: het lezen van die gegevens mislukte (eigen transactie, afgeschermd) —
    // alleen de interne melding valt terug, nooit de klantmail ernaast.
    'daten_fehler',
    // Order E1: de Objekt-Anfrage waarnaar de posten verwijst bestaat niet in
    // DEZE tenant — geen gegevens, geen mail (de router meldt het aan ops).
    'anfrage_fehlt',
    // Order E1: de aanvraag is een TESTAANVRAAG (stafdomein) — de render-service
    // weigert hem, ook als iemand het event of de render handmatig herhaalt.
    'anfrage_test',
];
export const mailRenderErgebnisSchema = z.discriminatedUnion('gefunden', [
    z.object({
        schluessel: z.string(),
        gefunden: z.literal(true),
        vorlageId: z.string().uuid(),
        version: z.number().int(),
        /** `tenant` = eigen laag van de mandant, `plattform` = holding-laag. */
        ebene: z.enum(['tenant', 'plattform']),
        betreff: z.string(),
        htmlContent: z.string(),
        /**
         * B6: de platte-tekstversie uit DEZELFDE gegevens — alleen de interne
         * meldingen dragen hem (de router geeft hem naast `htmlContent` aan Brevo).
         */
        textContent: z.string().optional(),
        sender: z.object({ name: z.string(), email: z.string() }),
        replyTo: z.string().nullable(),
        /**
         * Order E1: ALLEEN bij een DB-klantmail (E12) — de ontvanger zoals de
         * render-service hem uit de aanvraagrij las. Het event is PII-vrij, dus de
         * router kent het adres niet en stuurt de mail hierheen.
         */
        empfaenger: z.object({ email: z.string().email(), name: z.string() }).strict().optional(),
    }),
    z.object({
        schluessel: z.string(),
        gefunden: z.literal(false),
        grund: z.enum(MAIL_RENDER_GRUENDE),
    }),
]);
export const mailRenderAntwortSchema = z.object({ mails: z.array(mailRenderErgebnisSchema) });
/**
 * «Testmail an mich» (dossier §1.1): dezelfde render-weg, maar naar het eigen
 * adres van de aanvrager — nooit naar een ingetypt adres, zodat de knop geen
 * verzendkanaal wordt. Voorbeeldwaarden vult de server in.
 */
export const mailTestmailSchema = z.object({}).strict();
/**
 * `register` (PR-2): de mails die het backend uit het Vorlagen-register
 * rendert voor de router en voor «Testmail an mich» — het ledger
 * `mail_protokoll`, dat ook de WEIGERINGEN kent (`abgelehnt` + reden).
 */
export const PROTOKOLL_QUELLEN = ['newsletter', 'router', 'register'];
/**
 * De event-types die de n8n-router in een KLANT-/STAF-mail omzet (review #495
 * MINOR-4): het Protokoll toont uit `webhook_events` alleen deze, niet de hele
 * uitgaande envelop (break_glass.*, display_token.*, …). Bron: de router-cases
 * met een templateMail/mlgCustomerMail; `vorlagen-katalog.test.ts` pint dat elk
 * catalogus-event hierin staat.
 */
export const PROTOKOLL_MAIL_EVENTS = [
    'result.email',
    'consult.requested',
    'portal.invited',
    'auth.password_reset',
    'auth.login_alert',
    'reminder.due',
    'dossier.completed',
    'valuation.estimated',
    // Fase B3 (#492): de ene Bewertungsbericht-Erinnerung, register-gerenderd.
    'lead.report_reminder',
    'user.invited',
    'user.password_reset',
    // E9 Maklervertrag (Kundenportal #493, register-gerenderd sinds PR-2).
    'contract.sent',
    // E10 Exposé-Zusendung (Vermarktung A-4): de klantmail met de tokenlink naar
    // de Verkaufsdokumentation. Staat hier omdat het Protokoll precies de
    // KLANTmails toont — en `vorlagen-katalog.test.ts` pint dat elk event van een
    // catalogusrij in deze lijst staat, dus de rij en deze regel reizen samen.
    'expose.requested',
    // B6: de interne melding «Bewertung 1/2» komt sinds B6 uit het register
    // (`intern.bewertung_anfrage`) — een STAF-mail uit het register, dus hier.
    'valuation.lead.created',
    // Vor-Ort-Bewertung (docs/VOR-ORT-FORMULIER-CONTRACT.md §5.2): Mail 1, de
    // bevestiging aan de aanvrager (`vor_ort.bestaetigung`), rijdt op dit event.
    // Het event staat hier vóór zijn catalogusrij (die komt met de Mail-1-PR): de
    // pin is eenrichting — catalogus → lijst — dus deze volgorde is groen.
    'vor_ort.requested',
    // Order E1 (17-10): E12, de bevestiging van een Objekt-Anfrage mét kopie van
    // de gegevens (`e12.objekt_anfrage_bestaetigung`), rijdt op dit event.
    'objekt_anfrage.requested',
];
// ── Rechten (voorbereiding Benutzer-project, dossier §1.3) ───────────────────
/**
 * Permissie-constanten → rollen. Vandaag gespiegeld door de @Roles-decorators
 * op de Kommunikation-controllers en de accessControlProvider; het
 * Benutzer-project vervangt de rol-lijsten door deze sleutels.
 *
 * Board verplaatsen (`pipeline.board`) is voor ALLE stafrollen die het CRM
 * zien — bewust geen constante, want een lege beperking is geen recht.
 */
export const KOMMUNIKATION_RECHTE = {
    'kommunikation.vorlagen': ['admin', 'backoffice'],
    'kommunikation.senden': ['admin'],
    'pipeline.verwalten': ['admin'],
};
/** Rollen die het Kommunikation-gebied mogen LEZEN (register + protokoll). */
export const KOMMUNIKATION_LESEN = ['admin', 'advisor', 'backoffice', 'makler'];
export function hatKommunikationRecht(role, recht) {
    if (!role)
        return false;
    return KOMMUNIKATION_RECHTE[recht].includes(role);
}
